import { Controller } from "@hotwired/stimulus"

// AB-068 / F2 Commit 5+6 — Recommended tab Stimulus controller.
//
// Renders the per-post recommendation cards into `gridTarget` from the
// `/autoboosts/:id/recommendations.json` payload, and acts on a card when the
// customer clicks one of its two buttons: "Boost this post" (enqueue an ad for
// the post under this autoboost) or "New autoboost" (start a draft with the
// recommended goal and navigate into the wizard).
//
// Targets:
//   - grid:   container where rendered cards land
//   - status: the loading / working / empty status block
//
// Values:
//   - autoboostId:     UUID of the autoboost — used to build the endpoint URLs
//   - showUrl:         GET endpoint (overrides the autoboostId-based default)
//   - boostUrl:        POST endpoint for "Boost this post"
//   - newAutoboostUrl: POST endpoint for "New autoboost"
//
// Cards are server-rendered (RecommendedPostCardComponent) and arrive as
// `card_html` in the JSON — incl. the post image + admin-only "Why?" panel —
// so this controller only injects them; it builds no card markup itself.
export default class extends Controller {
  // status = the loading/empty/error display block; statusText + statusBar are
  // optional inner targets (centered message + infinite brand progress bar) —
  // plain markup with just `status` still works (text falls back onto it).
  static targets = ["grid", "status", "statusText", "statusBar"]
  static values  = {
    autoboostId:     String,
    showUrl:         String,
    boostUrl:        String,
    newAutoboostUrl: String,
  }

  connect() {
    console.log(`[recommendations] connect autoboost=${this.autoboostIdValue || "(none)"}`)
    this.refresh()
    this.boundRefresh = () => this.refresh()
    window.addEventListener("autoboost:recommendations-changed", this.boundRefresh)
  }

  disconnect() {
    console.log("[recommendations] disconnect")
    if (this.boundRefresh) {
      window.removeEventListener("autoboost:recommendations-changed", this.boundRefresh)
    }
  }

  async refresh() {
    const url = this.#showUrl()
    if (!url) {
      console.warn("[recommendations] no show url — refusing to fetch")
      return
    }

    console.log(`[recommendations] GET ${url}`)
    try {
      const res = await fetch(url, {
        method: "GET",
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      })
      if (!res.ok) {
        console.error(`[recommendations] ${res.status} on GET ${url}`)
        this.#renderError()
        return
      }
      const json = await res.json()
      this.#render(json?.data || {})
    } catch (err) {
      console.error("[recommendations] fetch failed", err)
      this.#renderError()
    }
  }

  // "Boost this post" — enqueue an ad for the recommended post under this
  // autoboost. Stays on the page; confirms with a toast.
  async boost(event) {
    event.preventDefault()
    const button = event.currentTarget
    const body = this.#paramsFor(button)

    const url = this.#boostUrl()
    if (!url) {
      console.warn("[recommendations] no boost url — refusing to boost")
      return
    }

    console.log(`[recommendations] POST ${url} (boost)`, body)
    this.#disable(button)

    try {
      const res = await this.#post(url, body)
      if (!res.ok) {
        const text = await res.text().catch(() => "")
        console.error(`[recommendations] boost ${res.status} ${text}`)
        this.#enable(button)
        this.#toast("Couldn't boost that post — try again in a moment", "error")
        return
      }
      console.log("[recommendations] boost ok")
      button.textContent = "Boosting…"
      this.#toast("We're boosting this post now — your ad will be live shortly", "success")
    } catch (err) {
      console.error("[recommendations] boost failed", err)
      this.#enable(button)
      this.#toast("Couldn't boost that post — try again in a moment", "error")
    }
  }

  // "New autoboost" — start a draft with the recommended goal, then navigate
  // into the wizard to finish setup.
  async createAutoboost(event) {
    event.preventDefault()
    const button = event.currentTarget
    const body = this.#paramsFor(button)

    const url = this.#newAutoboostUrl()
    if (!url) {
      console.warn("[recommendations] no new-autoboost url — refusing to create")
      return
    }

    console.log(`[recommendations] POST ${url} (new autoboost)`, body)
    this.#disable(button)

    try {
      const res = await this.#post(url, body)
      if (!res.ok) {
        const text = await res.text().catch(() => "")
        console.error(`[recommendations] new autoboost ${res.status} ${text}`)
        this.#enable(button)
        this.#toast("Couldn't start a new autoboost — try again in a moment", "error")
        return
      }
      const json = await res.json().catch(() => ({}))
      const target = json?.payload?.redirect_to
      if (target) {
        console.log(`[recommendations] new autoboost ok — navigating to ${target}`)
        window.location.href = target
      } else {
        console.warn("[recommendations] new autoboost ok but no redirect target")
        this.#enable(button)
      }
    } catch (err) {
      console.error("[recommendations] new autoboost failed", err)
      this.#enable(button)
      this.#toast("Couldn't start a new autoboost — try again in a moment", "error")
    }
  }

  // ── private ────────────────────────────────────────────────────

  #paramsFor(button) {
    const fbPostId = button.dataset.recommendationsFbPostIdParam || ""
    const fbInstagramMediumId = button.dataset.recommendationsFbInstagramMediumIdParam || ""
    const recommendedObjective = button.dataset.recommendationsRecommendedObjectiveParam || ""
    const body = {}
    if (recommendedObjective) body.recommended_objective = recommendedObjective
    if (fbPostId) body.fb_post_id = fbPostId
    if (fbInstagramMediumId) body.fb_instagram_medium_id = fbInstagramMediumId
    return body
  }

  #post(url, body) {
    return fetch(url, {
      method: "POST",
      headers: {
        "Accept":       "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "",
      },
      credentials: "same-origin",
      body: JSON.stringify(body),
    })
  }

  #disable(button) {
    button.disabled = true
    button.classList.add("opacity-50", "pointer-events-none")
  }

  #enable(button) {
    button.disabled = false
    button.classList.remove("opacity-50", "pointer-events-none")
  }

  #showUrl() {
    if (this.hasShowUrlValue && this.showUrlValue) return this.showUrlValue
    if (this.hasAutoboostIdValue && this.autoboostIdValue) {
      return `/autoboosts/${this.autoboostIdValue}/recommendations.json`
    }
    return null
  }

  #boostUrl() {
    if (this.hasBoostUrlValue && this.boostUrlValue) return this.boostUrlValue
    if (this.hasAutoboostIdValue && this.autoboostIdValue) {
      return `/autoboosts/${this.autoboostIdValue}/recommendations/boost`
    }
    return null
  }

  #newAutoboostUrl() {
    if (this.hasNewAutoboostUrlValue && this.newAutoboostUrlValue) return this.newAutoboostUrlValue
    if (this.hasAutoboostIdValue && this.autoboostIdValue) {
      return `/autoboosts/${this.autoboostIdValue}/recommendations/new_autoboost`
    }
    return null
  }

  #render(data) {
    const recs = Array.isArray(data.recommendations) ? data.recommendations : []
    const status = (data.status || "").toString()

    if (this.hasStatusTarget) {
      if (recs.length === 0 && status === "working") {
        // Only when the server CONFIRMED a recommender job is genuinely
        // active — keep the infinite progress bar running.
        this.#setStatus("We're working out our recommendations — this takes a moment", { bar: true })
      } else if (recs.length === 0) {
        // Neutral — we don't actually know WHY it's empty, and there's no
        // confirmed job, so don't promise results "shortly". No bar.
        this.#setStatus("No recommendations yet", { bar: false })
      } else {
        this.statusTarget.classList.add("hidden")
      }
    }

    if (!this.hasGridTarget) return

    if (recs.length === 0) {
      this.gridTarget.innerHTML = ""
      return
    }

    // Cards are server-rendered (RecommendedPostCardComponent) and arrive as
    // `card_html` so the Recommended tab matches the Eligible Posts design,
    // incl. the post image. This controller just injects them; the Boost /
    // New-autoboost data-actions resolve because the cards land inside this element.
    this.gridTarget.innerHTML = recs.map(r => r.card_html || "").join("")
  }

  #renderError() {
    if (this.hasStatusTarget) {
      this.#setStatus("Checking again in a few minutes", { bar: false })
    }
    if (this.hasGridTarget) this.gridTarget.innerHTML = ""
  }

  // Write the status message (into statusText when present, else the status
  // block itself) and toggle the infinite progress bar.
  #setStatus(message, { bar }) {
    const textEl = this.hasStatusTextTarget ? this.statusTextTarget : this.statusTarget
    textEl.textContent = message
    if (this.hasStatusBarTarget) this.statusBarTarget.classList.toggle("hidden", !bar)
    this.statusTarget.classList.remove("hidden")
  }

  #toast(message, severity = "error") {
    const container = document.getElementById("toast_container")
    if (!container) {
      console.warn(`[recommendations] toast (${severity}): ${message}`)
      return
    }
    const palette = severity === "success"
      ? "bg-green-50 border-green-200 text-green-800 dark:bg-green-900/30 dark:border-green-800 dark:text-green-200"
      : "bg-red-50 border-red-200 text-red-800 dark:bg-red-900/30 dark:border-red-800 dark:text-red-200"
    const el = document.createElement("div")
    el.setAttribute("data-controller", "toast")
    el.setAttribute("data-toast-severity-value", severity)
    el.setAttribute("data-toast-duration-value", "5000")
    el.className = `pointer-events-auto transform translate-x-full opacity-0 transition-all duration-300 ease-out rounded-lg shadow-lg border overflow-hidden ${palette}`
    el.innerHTML = `<div class="flex items-stretch"><div class="flex-1 p-4 text-sm font-medium">${this.#escape(message)}</div><button data-action="toast#dismiss" class="px-3 text-current opacity-50 hover:opacity-100 shrink-0 border-l border-current/10" aria-label="Dismiss">×</button></div>`
    container.appendChild(el)
  }

  #escape(value) {
    return String(value || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;")
  }
}
